// =====================================================
// COUNTERFACTUAL PROOF DISPLAY COMPONENT
// Shows "Capital Preserved" proof for NO_TRADE/WAIT decisions
// Now with REAL-TIME price tracking PER ASSET
// =====================================================

import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getCounterfactualForEntry } from '@/services/counterfactualService';
import type { CounterfactualTracking } from '@/types/counterfactual';
import { 
  formatCapitalPreserved, 
  getCapitalPreservedColor, 
  getCounterfactualLabel 
} from '@/types/counterfactual';

const { FiShield, FiTrendingDown, FiTrendingUp, FiClock, FiDollarSign, FiActivity, FiRefreshCw } = FiIcons;

interface CounterfactualProofProps {
  journalEntryId: string;
  assetSymbol?: string;
  marketType?: string;
  compact?: boolean;
  /** Bubble priceAtDecision + currentPrice up so the parent can render
      the price-journey sparkline next to its other visualizations. */
  onPriceUpdate?: (data: { priceAtDecision: number; currentPrice: number }) => void;
}

const CounterfactualProof: React.FC<CounterfactualProofProps> = ({
  journalEntryId,
  assetSymbol = 'XAUUSD',
  marketType = 'gold',
  compact = false,
  onPriceUpdate,
}) => {
  const [tracking, setTracking] = useState<CounterfactualTracking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch current price based on asset type.
  // BUG FIX: was using market_type only, so a BTCUSD trade saved with
  // market_type='gold' (data inconsistency) fetched gold price instead
  // of crypto -> 'Current Price' showed \$4k for BTC. Now auto-detect
  // market from the SYMBOL (single source of truth).
  const fetchCurrentPrice = async (symbol: string, market: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Auto-detect from symbol — overrides any inconsistent market_type
      const up = (symbol || '').toUpperCase();
      const CRYPTO_BASES = ['BTC','ETH','SOL','XRP','ADA','DOGE','AVAX','LINK','MATIC','DOT'];
      const INDICES_SYMBOLS = ['US30','NAS100','SPP500','GER30'];
      let detectedMarket: 'gold' | 'crypto' | 'indices' | null = null;
      if (up.startsWith('XAU')) detectedMarket = 'gold';
      else if (CRYPTO_BASES.some((b) => up.includes(b))) detectedMarket = 'crypto';
      else if (INDICES_SYMBOLS.includes(up)) detectedMarket = 'indices';
      else detectedMarket = (market || '').toLowerCase() as any;

      if (detectedMarket === 'gold') {
        const response = await fetch(`${supabaseUrl}/functions/v1/get-gold-price`, {
          headers: { 'Authorization': `Bearer ${supabaseKey}` },
        });
        const data = await response.json();
        if (data.price) { setCurrentPrice(data.price); return data.price; }
      } else if (detectedMarket === 'crypto') {
        const baseSymbol = up.replace('USD', '');
        const response = await fetch(`${supabaseUrl}/functions/v1/get-crypto-prices?symbols=${baseSymbol}`, {
          headers: { 'Authorization': `Bearer ${supabaseKey}` },
        });
        const data = await response.json();
        if (data.prices && data.prices[baseSymbol]?.price) {
          const price = data.prices[baseSymbol].price;
          setCurrentPrice(price); return price;
        }
      } else if (detectedMarket === 'indices') {
        const response = await fetch(`${supabaseUrl}/functions/v1/get-indices-prices`, {
          headers: { 'Authorization': `Bearer ${supabaseKey}` },
        });
        const data = await response.json();
        if (data.prices && data.prices[up]?.price) {
          const price = data.prices[up].price;
          setCurrentPrice(price); return price;
        }
      }
    } catch (err) {
      console.error(`Error fetching current price for ${symbol}:`, err);
    }
    return null;
  };

  // Calculate real-time metrics based on current price
  const realTimeMetrics = useMemo(() => {
    if (!tracking || !currentPrice) return null;
    
    const { priceAtDecision, directionIfTraded, hypotheticalStopLoss, hypotheticalRiskAmount } = tracking;
    
    if (!directionIfTraded || !hypotheticalStopLoss) return null;
    
    const priceChange = currentPrice - priceAtDecision;
    const stopDistance = Math.abs(priceAtDecision - hypotheticalStopLoss);
    const riskPerR = hypotheticalRiskAmount || 100;
    
    let capitalPreservedR = 0;
    
    if (directionIfTraded === 'LONG') {
      if (priceChange < 0) {
        capitalPreservedR = Math.abs(priceChange) / stopDistance;
      } else {
        capitalPreservedR = -(priceChange / stopDistance);
      }
    } else {
      if (priceChange > 0) {
        capitalPreservedR = priceChange / stopDistance;
      } else {
        capitalPreservedR = -(Math.abs(priceChange) / stopDistance);
      }
    }
    
    capitalPreservedR = Math.max(-5, Math.min(5, capitalPreservedR));
    const capitalPreservedUsd = capitalPreservedR * riskPerR;
    
    return {
      capitalPreservedR: parseFloat(capitalPreservedR.toFixed(2)),
      capitalPreservedUsd: parseFloat(capitalPreservedUsd.toFixed(2)),
      priceChange: parseFloat(priceChange.toFixed(2)),
    };
  }, [tracking, currentPrice]);

  useEffect(() => {
    const loadTracking = async () => {
      setIsLoading(true);
      try {
        const data = await getCounterfactualForEntry(journalEntryId);
        setTracking(data);
        
        // Fetch current price for the specific asset
        if (data) {
          await fetchCurrentPrice(assetSymbol, marketType);
        }
      } catch (error) {
        console.error('Error loading counterfactual tracking:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTracking();
  }, [journalEntryId, assetSymbol, marketType]);

  // Bubble price data up to the parent so it can render visualizations
  // (price-journey sparkline) using the same fetched values.
  useEffect(() => {
    if (onPriceUpdate && tracking?.priceAtDecision != null && currentPrice != null) {
      onPriceUpdate({ priceAtDecision: tracking.priceAtDecision, currentPrice });
    }
  }, [tracking?.priceAtDecision, currentPrice, onPriceUpdate]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCurrentPrice(assetSymbol, marketType);
    setIsRefreshing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />
        <span>Analyzing counterfactual...</span>
      </div>
    );
  }

  if (!tracking) {
    return null;
  }

  const { 
    priceAtDecision,
    priceAfter1h,
    priceAfter4h,
    priceAfter24h,
    trackingStatus,
    directionIfTraded
  } = tracking;

  // Use real-time metrics if available, otherwise fallback to stored values
  const displayCapitalR = realTimeMetrics?.capitalPreservedR ?? tracking.capitalPreservedR;
  const displayCapitalUsd = realTimeMetrics?.capitalPreservedUsd ?? tracking.capitalPreservedUsd;
  const hasRealTimeData = realTimeMetrics !== null && currentPrice !== null;

  // Compact version for list view
  if (compact) {
    const showCapital = hasRealTimeData || trackingStatus === 'COMPLETED';
    if (!showCapital) {
      return (
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <SafeIcon icon={FiClock} className="w-3 h-3" />
          Tracking...
        </span>
      );
    }

    return (
      <span className={`text-xs font-semibold flex items-center gap-1 ${getCapitalPreservedColor(displayCapitalR)}`}>
        <SafeIcon icon={FiShield} className="w-3 h-3" />
        {formatCapitalPreserved(displayCapitalR, null)}
      </span>
    );
  }

  // Full version for detail modal
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl overflow-hidden border ${
        displayCapitalR && displayCapitalR > 0 
          ? 'border-emerald-500/30' 
          : displayCapitalR && displayCapitalR < 0
            ? 'border-amber-500/30'
            : 'border-blue-500/30'
      }`}
    >
      {/* Header */}
      <div className={`p-4 ${
        displayCapitalR && displayCapitalR > 0 
          ? 'bg-emerald-500/10' 
          : displayCapitalR && displayCapitalR < 0
            ? 'bg-amber-500/10'
            : 'bg-blue-500/10'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SafeIcon 
              icon={FiShield} 
              className={`w-6 h-6 ${getCapitalPreservedColor(displayCapitalR)}`} 
            />
            <div>
              <span className={`text-lg font-bold ${getCapitalPreservedColor(displayCapitalR)}`}>
                {hasRealTimeData ? getCounterfactualLabel(displayCapitalR) : 'Analyzing...'}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {hasRealTimeData ? 'Real-time Analysis' : 'Loading price data...'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasRealTimeData && displayCapitalR !== null && (
              <div className="text-right">
                <div className={`text-2xl font-bold ${getCapitalPreservedColor(displayCapitalR)}`}>
                  {displayCapitalR >= 0 ? '+' : ''}{displayCapitalR.toFixed(1)}R
                </div>
                {displayCapitalUsd !== null && (
                  <div className="text-xs text-gray-500">
                    {displayCapitalUsd >= 0 ? '+' : ''}${Math.abs(displayCapitalUsd).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
              title="Refresh price"
            >
              <SafeIcon 
                icon={FiRefreshCw} 
                className={`w-4 h-4 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 bg-gray-800/30 space-y-4">
        {/* What Happened Section */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            What Happened After Your Decision
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-800/50 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">At Decision</div>
              <div className="text-sm font-bold text-white tabular-nums">
                ${priceAtDecision.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            
            {currentPrice && (
              <div className="p-3 bg-gray-800/50 rounded-lg border border-purple-500/30">
                <div className="text-xs text-purple-400 mb-1">Current Price</div>
                <div className={`text-sm font-bold tabular-nums ${
                  currentPrice > priceAtDecision ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
            
            {realTimeMetrics && (
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Price Change</div>
                <div className={`text-sm font-bold tabular-nums ${
                  realTimeMetrics.priceChange > 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {realTimeMetrics.priceChange > 0 ? '+' : ''}${realTimeMetrics.priceChange.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hypothetical Trade Analysis */}
        {directionIfTraded && hasRealTimeData && (
          <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
            <div className="text-xs text-gray-500 mb-2">
              Based on AI analysis bias ({directionIfTraded}):
            </div>
            <div className={`text-sm font-medium ${getCapitalPreservedColor(displayCapitalR)}`}>
              {displayCapitalR && displayCapitalR > 0 
                ? `Price moved against the ${directionIfTraded} bias. You avoided a potential ${displayCapitalR.toFixed(1)}R loss.`
                : displayCapitalR && displayCapitalR < 0
                  ? `Price moved in favor of ${directionIfTraded}. Missed opportunity: ${Math.abs(displayCapitalR).toFixed(1)}R. Discipline > single trades.`
                  : 'Price moved sideways. No significant impact.'}
            </div>
          </div>
        )}

        {/* Loading indicator if no real-time data */}
        {!hasRealTimeData && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <div className="animate-pulse">
              <SafeIcon icon={FiActivity} className="w-4 h-4" />
            </div>
            <span>Fetching current price...</span>
          </div>
        )}
      </div>

      {/* Philosophy Banner — NOTE: use Number()>0 not `x && x>0` because
          when displayCapitalR is 0, the short-circuit returns literal 0
          and JSX renders it as the character "0" (classic React pitfall). */}
      {Number(displayCapitalR) > 0 && (
        <div className="p-3 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-t border-white/5">
          <p className="text-xs text-center text-gray-400 italic">
            "Anyone can take a trade. Professionals know when NOT to."
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default CounterfactualProof;
